"use client";
import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { createDateFormat, createNumberFormat, type DateFormat, type NumberFormat } from "./format";
import { DEFAULT_FORMAT_LOCALE, DEFAULT_LOCALE } from "./locales";
import { en, type Messages } from "./messages/en";
import { createTranslate, type Translate } from "./translate";

type I18nContextValue = { locale: string; formatLocale: string; messages: Messages };

// Without a provider (unit tests of a component) the app speaks English.
const I18nContext = createContext<I18nContextValue>({ locale: DEFAULT_LOCALE, formatLocale: DEFAULT_FORMAT_LOCALE, messages: en });

/**
 * Mounted once by the root layout with what the server negotiated from the request: the catalogue to use
 * (`locale`, `messages`) and the language tag for dates and numbers (`formatLocale`).
 */
export function I18nProvider({ locale, formatLocale, messages, children }: I18nContextValue & { children: ReactNode }) {
    const value = useMemo(() => ({ locale, formatLocale, messages }), [locale, formatLocale, messages]);
    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

const subscribeNever = () => () => {};
/** false while rendering on the server and during hydration, true afterwards: no mismatch, one extra render. */
function useMounted(): boolean {
    return useSyncExternalStore(
        subscribeNever,
        () => true,
        () => false,
    );
}

export type I18n = {
    locale: string;
    t: Translate;
    date: DateFormat;
    number: NumberFormat;
};

/**
 * Translation, dates and numbers for a client component.
 * Dates and numbers: UTC and the request's language on the server and during hydration (identical text, no
 * hydration warning); the browser's locale and time zone from the first effect on.
 */
export function useI18n(): I18n {
    const { locale, formatLocale, messages } = useContext(I18nContext);
    const mounted = useMounted();
    const t = useMemo(() => createTranslate(locale, messages), [locale, messages]);
    return useMemo(() => {
        const browserLocale =
            mounted && typeof navigator !== "undefined" ? (navigator.languages?.[0] ?? navigator.language) : undefined;
        const tag = browserLocale || formatLocale;
        return {
            locale,
            t,
            date: mounted ? createDateFormat(tag) : createDateFormat(formatLocale, "UTC", { mounted: false }),
            number: createNumberFormat(tag),
        };
    }, [locale, formatLocale, mounted, t]);
}

/** Just the translator. */
export const useT = (): Translate => useI18n().t;
